// ------------------------------
// components/details/holdInvoiceCard.tsx
// One hold invoice: the "Needs your decision" card (Accept / Reject), the
// "Waiting for payer" card (QR / Cancel) and the compact Recent row.
// zz-support-hold-invoices-fable.txt 10.3, 10.5
// ------------------------------

import { observer } from "mobx-react";
import React, { useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Progress,
  Stack,
  Text,
  Tooltip,
} from "@mantine/core";
import {
  IconCheck,
  IconX,
  IconClock,
  IconQrcode,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { formatDistanceToNow } from "date-fns";
import { Bob } from "../../stores/Bob";
import {
  HoldInvoice,
  formatCountdown,
  holdAmountSatoshis,
  holdOutcome,
} from "../../helpers/holdInvoices";
import UserCreatedInvoiceQR from "../modals/helpers/UserCreatedInvoiceQR";

// translucent dark surfaces only (AGENTS.md: no white blocks on dark pages)
export const holdGlass = {
  background: "rgba(20,20,20,.45)",
  border: "1px solid rgba(255,255,255,.06)",
  backdropFilter: "blur(8px)",
  WebkitBackdropFilter: "blur(8px)",
  borderRadius: 18,
} as const;

// re-renders the caller every `intervalMs` so countdowns tick
export const useNow = (intervalMs = 1000) => {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(id);
  }, [intervalMs]);
  return now;
};

const OriginBadge = ({ hold }: { hold: HoldInvoice }) =>
  hold.origin === "nwc" ? (
    <Badge variant="light" color="violet">
      NWC {hold.origin_name ? `"${hold.origin_name}"` : ""}
    </Badge>
  ) : (
    <Badge variant="light" color="blue">
      Web
    </Badge>
  );

const AmountHeader = observer(
  ({ bob, hold }: { bob: Bob; hold: HoldInvoice }) => {
    const sats = holdAmountSatoshis(hold);
    return (
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Stack gap={0}>
          <Text fw={800} fz={26} style={{ letterSpacing: -0.5 }}>
            {sats.toLocaleString()}{" "}
            <Text span c="dimmed" fz="md" fw={500}>
              sats
            </Text>
          </Text>
          <Text c="gray.4" fz="sm">
            {bob.formatFiat(sats)}
          </Text>
        </Stack>
        <OriginBadge hold={hold} />
      </Group>
    );
  },
);

// ---------------------------------------------------------------------------
// accepted: Accept / Reject
// ---------------------------------------------------------------------------

export const HoldDecisionCard = observer(
  ({
    bob,
    hold,
    onDecideLater,
  }: {
    bob: Bob;
    hold: HoldInvoice;
    onDecideLater?: () => void;
  }) => {
    const now = useNow(1000);
    const [isConfirmingReject, setIsConfirmingReject] = useState(false);
    const settings = bob.workspace.holdInvoiceSettings?.data;
    const busy = bob.holdInvoiceBusyUuid === hold.uuid;
    const error = bob.holdInvoiceErrors.data[hold.uuid];

    const deadline = hold.hold_decision_deadline_at ?? now;
    const msLeft = Math.max(0, deadline - now);
    const windowMs = (settings?.decision_window_seconds ?? 0) * 1000;
    const percentLeft =
      windowMs > 0 ? Math.max(0, Math.min(100, (msLeft / windowMs) * 100)) : 0;
    const isUrgent = msLeft < 10 * 60 * 1000;
    const decided = hold.settle_pending || hold.cancel_pending;

    return (
      <Card p="lg" style={holdGlass}>
        <Stack gap="sm">
          <AmountHeader bob={bob} hold={hold} />

          {hold.our_provided_description && (
            <Text fz="sm">{hold.our_provided_description}</Text>
          )}
          {hold.hold_accepted_at && (
            <Text fz="xs" c="dimmed">
              Arrived{" "}
              {formatDistanceToNow(new Date(hold.hold_accepted_at), {
                addSuffix: true,
              })}
            </Text>
          )}

          {!decided && (
            <Stack gap={6} mt="xs">
              <Group justify="space-between" align="baseline" wrap="nowrap">
                <Text fz="sm" fw={600} c="gray.3">
                  Time left to decide
                </Text>
                <Text
                  fz="xl"
                  fw={800}
                  c={isUrgent ? "red.4" : "yellow.4"}
                  style={{ fontVariantNumeric: "tabular-nums" }}
                >
                  {formatCountdown(msLeft)}
                </Text>
              </Group>
              <Progress
                value={percentLeft}
                color={isUrgent ? "red" : "yellow"}
                size="md"
                radius="xl"
                striped={isUrgent}
                animated={isUrgent}
                aria-label="Time left to decide"
              />
            </Stack>
          )}

          {hold.htlc_count > 1 && (
            <Alert
              variant="light"
              color="orange"
              icon={<IconAlertTriangle size={18} />}
            >
              This payment arrived as {hold.htlc_count} separate payments (an
              old-style duplicate). Accepting takes all of them.
            </Alert>
          )}

          {hold.settle_pending && (
            <Badge color="yellow" variant="light" size="lg">
              Accepting…
            </Badge>
          )}
          {hold.cancel_pending && (
            <Badge color="yellow" variant="light" size="lg">
              {hold.hold_cancel_reason === "policy_cap"
                ? "Refused: too many payments are waiting for a decision. Refunding the payer…"
                : "Rejecting…"}
            </Badge>
          )}

          {error && (
            <Alert variant="light" color="red" icon={<IconX size={18} />}>
              {error}
            </Alert>
          )}

          {!decided && !isConfirmingReject && (
            <Group gap="xs" grow mt="xs">
              <Tooltip
                label="Only the connected app can accept this payment"
                disabled={hold.can_settle}
              >
                <Button
                  color="green"
                  leftSection={<IconCheck size={16} />}
                  disabled={!hold.can_settle || busy}
                  loading={busy}
                  onClick={() =>
                    bob.holdInvoiceAction("settle-hold-invoice", hold.uuid)
                  }
                >
                  Accept payment
                </Button>
              </Tooltip>
              <Button
                color="red"
                variant="light"
                leftSection={<IconX size={16} />}
                disabled={!hold.can_cancel || busy}
                onClick={() => {
                  bob.workspace.markHoldModalInteraction();
                  setIsConfirmingReject(true);
                }}
              >
                Reject payment
              </Button>
            </Group>
          )}
          {!decided && !isConfirmingReject && onDecideLater && (
            <Button variant="subtle" color="gray" fullWidth onClick={onDecideLater}>
              Decide later
            </Button>
          )}

          {!decided && isConfirmingReject && (
            <Stack gap="xs">
              <Alert variant="light" color="red" title="Reject this payment?">
                {hold.origin === "nwc" ? (
                  <>
                    {hold.origin_name ? `"${hold.origin_name}"` : "The app"}{" "}
                    created this invoice and is NOT told when you reject it. If
                    it is holding this payment for a trade or escrow, rejecting
                    refunds the payer while the trade may carry on.
                  </>
                ) : (
                  <>
                    The payer gets their money back. Their wallet will show the
                    payment as failed.
                  </>
                )}
              </Alert>
              <Group gap="xs">
                <Button
                  color="red"
                  leftSection={<IconX size={16} />}
                  loading={busy}
                  disabled={busy}
                  onClick={() => {
                    setIsConfirmingReject(false);
                    bob.holdInvoiceAction("cancel-hold-invoice", hold.uuid);
                  }}
                >
                  Yes, reject
                </Button>
                <Button
                  variant="default"
                  onClick={() => setIsConfirmingReject(false)}
                >
                  Keep it
                </Button>
              </Group>
            </Stack>
          )}

          {decided && onDecideLater && (
            <Group>
              <Button variant="subtle" color="gray" onClick={onDecideLater}>
                Close
              </Button>
            </Group>
          )}

          {!decided && (
            <Text fz="xs" c="dimmed" ta="center">
              {msLeft > 0
                ? "If you do nothing, we reject it for you and the payer gets their money back."
                : "The time to decide is up. We are rejecting it for you and the payer gets their money back."}
            </Text>
          )}
        </Stack>
      </Card>
    );
  },
);

// ---------------------------------------------------------------------------
// open: waiting for the payer
// ---------------------------------------------------------------------------

export const HoldOpenCard = observer(
  ({ bob, hold }: { bob: Bob; hold: HoldInvoice }) => {
    const now = useNow(30_000);
    const [showQr, setShowQr] = useState(false);
    const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
    const busy = bob.holdInvoiceBusyUuid === hold.uuid;
    const error = bob.holdInvoiceErrors.data[hold.uuid];

    return (
      <Card p="lg" style={holdGlass}>
        <Stack gap="sm">
          <AmountHeader bob={bob} hold={hold} />
          {hold.our_provided_description && (
            <Text fz="sm">{hold.our_provided_description}</Text>
          )}
          {hold.expires_at && (
            <Group gap={6}>
              <IconClock size={16} />
              <Text fz="sm" c="dimmed">
                {hold.expires_at > now
                  ? `Expires ${formatDistanceToNow(new Date(hold.expires_at), { addSuffix: true })}`
                  : "Expired"}
              </Text>
            </Group>
          )}

          {hold.cancel_pending && (
            <Badge color="yellow" variant="light" size="lg">
              Canceling…
            </Badge>
          )}

          {error && (
            <Alert variant="light" color="red" icon={<IconX size={18} />}>
              {error}
            </Alert>
          )}

          {showQr && hold.pay_request && (
            <UserCreatedInvoiceQR bob={bob} invoice={hold.pay_request} />
          )}

          {!hold.cancel_pending && !isConfirmingCancel && (
            <Group gap="xs">
              {hold.pay_request && (
                <Button
                  variant="default"
                  leftSection={<IconQrcode size={16} />}
                  onClick={() => setShowQr(!showQr)}
                >
                  {showQr ? "Hide invoice" : "Show invoice"}
                </Button>
              )}
              <Button
                variant="light"
                color="red"
                disabled={!hold.can_cancel || busy}
                onClick={() => setIsConfirmingCancel(true)}
              >
                Cancel invoice
              </Button>
            </Group>
          )}

          {!hold.cancel_pending && isConfirmingCancel && (
            <Group gap="xs">
              <Text fz="sm">Cancel this invoice? Nobody will be able to pay it.</Text>
              <Button
                color="red"
                size="xs"
                loading={busy}
                onClick={() => {
                  setIsConfirmingCancel(false);
                  bob.holdInvoiceAction("cancel-hold-invoice", hold.uuid);
                }}
              >
                Yes, cancel
              </Button>
              <Button
                variant="default"
                size="xs"
                onClick={() => setIsConfirmingCancel(false)}
              >
                Keep it
              </Button>
            </Group>
          )}
        </Stack>
      </Card>
    );
  },
);

// ---------------------------------------------------------------------------
// recent: settled / canceled / expired in the last 7 days
// ---------------------------------------------------------------------------

export const HoldRecentRow = observer(
  ({ bob, hold }: { bob: Bob; hold: HoldInvoice }) => {
    const outcome = holdOutcome(hold);
    const when =
      hold.confirmed_at ?? hold.hold_canceled_at ?? hold.expires_at ?? hold.created_at;
    return (
      <Group
        justify="space-between"
        wrap="nowrap"
        px="sm"
        py={8}
        style={{ borderBottom: "1px solid rgba(255,255,255,.06)" }}
      >
        <Stack gap={0} style={{ minWidth: 0 }}>
          <Text fw={600} fz="sm">
            {holdAmountSatoshis(hold).toLocaleString()} sats
          </Text>
          <Text
            fz="xs"
            c="dimmed"
            style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {hold.our_provided_description || (hold.origin === "nwc" ? `NWC ${hold.origin_name ?? ""}` : "Web")}
            {" · "}
            {formatDistanceToNow(new Date(when), { addSuffix: true })}
          </Text>
        </Stack>
        <Badge variant="light" color={outcome.color} style={{ flexShrink: 0 }}>
          {outcome.label}
        </Badge>
      </Group>
    );
  },
);
